import {
  maxMobile,
  maxWidth,
  primaryFont,
  primaryHeadingColor,
  retina,
  scrollbar,
  secondaryFont,
  wLight,
  wMBold,
  wMedium,
  xmMobile,
} from 'Utils/utilsStyle';

import { Modal, Switch } from 'antd';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { TOOLTIP_DOCUMENT_HUB } from 'Utils/Constants';
import { useDispatch, useSelector } from 'react-redux';
import { selectCreateFolderPopupData } from 'Selector/DocumentHubSelector';
import { actions } from 'Slice/DocumentHubSlice';
import classNames from 'classnames';
import InputWithRef from '../components/InputWithRef';
import HeadingWithToolTip from '../components/HeadingWithToolTip';

const StyledModal = styled(Modal)`
 &.ant-modal {
  @media all and (max-width: ${maxWidth}), ${retina} {
    width: 470px !important;
  }
  .ant-modal-content {
    .ant-modal-close {
      display: none;
    }
    .ant-modal-body {
      padding: 27px 30px 0px !important;
      .header-popup-div {
        display: flex;
        justify-content: space-between;
        .header-title {
          color: ${primaryHeadingColor};
          font-family: ${secondaryFont};
          font-size: 32px;
          font-weight: ${wMBold};
          line-height: 47px;
          letter-spacing: -1px;
          margin-bottom: 0;
          @media all and (max-width: ${maxWidth}), ${retina} {
            font-size: 28px;
            line-height: normal;
          }
        }
        .toggle-div {
          display: flex;
          align-items: center;
          .global-tooltip-element {
            width: 12px;
            height: 12px;
            .section-detail-tip {
              width: inherit;
              height: inherit;
              margin-left: 0;
              svg {
                width: inherit;
                height: inherit;
              }
              .ant-tooltip {
                .ant-tooltip-content {
                  width: 320px;
                  min-width: 320px;
                @media all and (max-width: ${maxWidth}), ${retina} {
                  width: 285px;
                  max-width: 285px;
                  min-width: 285px;
                }
                @media all and (max-width: ${maxMobile}) {
                  width: 250px;
                  max-width: 250px;
                }
                @media all and (max-width: ${xmMobile}) {
                  min-width: 250px;
                }
                  .tooltip_wrapper {
                    max-height: 75px;
                    overflow-y: auto;
                    white-space: normal;
                    ${scrollbar};
                    ::-webkit-scrollbar-track {
                      border-radius: 20px;
                    }
                    ::-webkit-scrollbar-thumb {
                      border-radius: 20px;
                    }
                  }
                }
              }
            }
          }
          .toggle-title {
            color: #5E7079;
            font-family: ${primaryFont};
            font-size: 16px;
            font-weight: ${wMedium};
            line-height: 18px;
            letter-spacing: -0.333px; 
            margin: 0px 12px 0px 8px;
            @media all and (max-width: ${maxWidth}), ${retina} {
              font-size: 14px;
              line-height: normal;
            }
          }
          .switch-class {
            rotate: 180deg;
          }
        }
      }
      .input-with-ref-style-wrapper {
        position: relative;
        .auth-input-label {
          margin: 0px;
          position: absolute;
          top: 50px;
           .error-msg  {
            display: flex;
            align-items: center;
            color: #F43C45;
            font-family: ${primaryFont};
            font-style: normal;
            font-weight: ${wMedium};
            .info-icon-red {
              width: 12px;
              height: 12px;
              margin-right: 5px;
            }
            .text-for-error {
              line-height: normal;
            }
          }
        }
        .auth-input {
          margin: 12px 0 30px 0;
        }
        .auth-input.error {
          border: 1.3px solid #F43C45 !important;
        }
        input::placeholder {
          font-weight: ${wLight};
        }
      }
    }
    .ant-modal-footer {
      padding: 10px 25px 25px;
      button {
        &:hover {
          outline: none;
        }
        font-family: ${primaryFont};
        line-height: normal;
        border-radius: 25px;
        outline: none;
        height: 36px;
        margin: 0px 4px;
        padding: 8px 17px;
        min-width: 110px;
        @media all and (max-width: ${maxWidth}), ${retina} {
          height: 32px;
          font-size: 14px;
          line-height: normal;
        }
      }
    }
  }
 }
`;

const CreateFolderPopup = function CreateFolderPopup() {
  const {
    folderName, visible, gilroyToggle, creating, error,
  } = useSelector(selectCreateFolderPopupData);
  const nameRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    e.preventDefault();
    dispatch(actions.setCreateFolderName(value.slice(0, 60)));
    if (error) {
      dispatch(actions.setCreateFolderPopupError(''));
    }
  };

  const onOk = () => {
    if (!creating) {
      dispatch(actions.createFolder({ name: folderName.trim(), gilroyToggle: gilroyToggle ? 1 : 0 }));
    }
  };

  const onCancel = () => {
    if (!creating) {
      dispatch(actions.setCreateFolderPopupData({
        folderName: '', gilroyToggle: true, visible: false, creating: false, error: '',
      }));
    }
  };

  return (
    <StyledModal
      open={visible}
      centered
      width={528}
      getContainer=""
      wrapClassName="create-folder-popups"
      onCancel={onCancel}
      closable
      okText="Create"
      cancelText="Cancel"
      onOk={onOk}
      okButtonProps={{ className: classNames('', { disabled: !folderName.trim() }) }}
    >
      <div className="header-popup-div">
        <h3 className="header-title">Create a Folder</h3>
        <div className="toggle-div">
          <HeadingWithToolTip
            toolTipText={TOOLTIP_DOCUMENT_HUB}
            toolPlacement="bottom"
          />
          <span className="toggle-title">Include in Gilroy</span>
          <Switch className="switch-class" checked={!gilroyToggle} onChange={(flag) => dispatch(actions.setCreateFolderPopupGilroyToggle(!flag))} />
        </div>
      </div>
      <InputWithRef
        inputLabel=""
        inputName=""
        isDisabled={false}
        inputType="text"
        inputPlaceholder="Write folder name"
        inputValue={folderName}
        onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
        onInputBlur={() => { }}
        error={error ?? ''}
        ref={nameRef}
        inputClassName="name"
        isPassword={false}
        onInputTogglePassword={() => { }}
        autocomplete=""
        errorIcon
        maxLength={60}
      />
    </StyledModal>
  );
};

export default CreateFolderPopup;
